import { CloseCircleFilled, EditFilled } from '@ant-design/icons';
import { Button, Descriptions, Popconfirm, Space, Tag } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';

import {
  useDefaultClassCreate,
  useDefaultClassRemove,
  useDefaultClassUpdate,
} from '../../hooks/apiHooks';
import { DefaultClass } from '../../models/DefaultClass';
import Container from '../common/Container';
import EditButton from '../common/EditButton';
import ListHeader from '../common/ListHeader';
import DefaultClassModal from '../modals/DefaultClassModal';

interface IProps {
  id?: string;
  defaultClasses: DefaultClass[];
}

const DefaultClasses: React.FC<IProps> = ({ defaultClasses, id }) => {
  const createMutation = useDefaultClassCreate();
  const updateMutation = useDefaultClassUpdate();
  const removeMutation = useDefaultClassRemove();

  const [isEditing, setIsEditing] = useState(false);
  const [editableClass, setEditableClass] = useState<DefaultClass>();
  const [deletableClass, setDeletableClass] = useState<DefaultClass>();
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);

  const onAdd = useCallback(
    (classification: DefaultClass) => {
      createMutation.mutate(classification);
    },
    [createMutation]
  );

  const onUpdate = useCallback(
    (classification: DefaultClass) => {
      updateMutation.mutate(classification);
    },
    [updateMutation]
  );

  const onDelete = useCallback(() => {
    if (deletableClass) {
      removeMutation.mutate(deletableClass);
    }
  }, [deletableClass, removeMutation]);

  const onEdit = useCallback((classification: DefaultClass) => {
    setEditableClass(classification);
    setShowEditModal(true);
  }, []);

  useEffect(() => {
    if (!isEditing) {
      setDeletableClass(undefined);
    }
  }, [isEditing]);

  useEffect(() => {
    if (createMutation.isSuccess) {
      setShowAddModal(false);
    }
  }, [createMutation.isSuccess]);

  useEffect(() => {
    if (updateMutation.isSuccess) {
      setShowEditModal(false);
    }
  }, [updateMutation.isSuccess]);

  return (
    <Container id={id}>
      <Descriptions
        colon={false}
        column={7}
        layout="vertical"
        title={
          <ListHeader>
            <Space direction="horizontal">
              <span>Default Vehicle Classes</span>
            </Space>

            <Space>
              <EditButton
                color={isEditing ? '#389e0d' : undefined}
                onClick={() => setIsEditing(!isEditing)}
              >
                {isEditing ? 'Done' : 'Edit'}
              </EditButton>
              <Button
                ghost
                style={{ width: 120 }}
                type="primary"
                onClick={() => setShowAddModal(true)}
              >
                Add Class
              </Button>
            </Space>
          </ListHeader>
        }
      >
        <Descriptions.Item>
          {defaultClasses.map((classification) => (
            <Tag key={classification.id} className="vehicle-tag" color="#262626">
              <Space direction="horizontal">
                {classification.name}
                {isEditing && (
                  <>
                    <EditFilled onClick={() => onEdit(classification)} />
                    <Popconfirm
                      okButtonProps={{ loading: removeMutation.isLoading }}
                      title="Are you sure to delete this class?"
                      visible={deletableClass?.id === classification.id}
                      onCancel={() => setDeletableClass(undefined)}
                      onConfirm={onDelete}
                    >
                      <CloseCircleFilled
                        onClick={() => setDeletableClass(classification)}
                      />
                    </Popconfirm>
                  </>
                )}
              </Space>
            </Tag>
          ))}
        </Descriptions.Item>
      </Descriptions>

      {showAddModal && (
        <DefaultClassModal
          error={createMutation.error}
          submitting={createMutation.isLoading}
          onClose={() => setShowAddModal(false)}
          onSubmit={onAdd}
        />
      )}
      {showEditModal && editableClass && (
        <DefaultClassModal
          error={updateMutation.error}
          selectedDefaultClass={editableClass}
          submitting={updateMutation.isLoading}
          onClose={() => setShowEditModal(false)}
          onSubmit={onUpdate}
        />
      )}
    </Container>
  );
};

export default DefaultClasses;
